import { RiAlarmWarningFill } from '@remixicon/react'

import { useScanWarningStore } from '@/store/scan-warning'

/**
 * Centered red dialog fired by `useScanWarningStore.flash(...)` whenever a
 * scan is rejected. Phil asked for a hard-to-miss visual cue alongside the
 * passive toast. Sits over a dimmed backdrop instead of washing the whole
 * screen red — still impossible to ignore but less aggressive when an
 * operator is mid-flow.
 *
 * Persists until the next scan replaces it or the operator touches anywhere
 * on screen — the old auto-dismiss vanished before operators noticed it and
 * they misplaced bundles.
 */
export const ScanWarningOverlay = () => {
  const warning = useScanWarningStore((s) => s.warning)
  const clear = useScanWarningStore((s) => s.clear)

  if (!warning) return null

  return (
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-6 backdrop-blur-sm'
      onClick={clear}
      role='alert'
      aria-live='assertive'
    >
      {/*
        Inline color: the project's Tailwind theme resets `--color-*: initial`
        and only redefines a custom palette (red-300 / red-400 etc.). Use the
        project's red-400 token so the dialog is reliably red regardless of
        theme regeneration.
      */}
      <div
        className='text-white-100 flex w-full max-w-lg flex-col items-center gap-4 rounded-2xl p-8 text-center shadow-2xl'
        style={{ backgroundColor: 'var(--red-400)' }}
      >
        <RiAlarmWarningFill className='size-14 animate-pulse' />
        <div className='text-3xl font-black tracking-tight uppercase'>
          {warning.title ?? 'Wrong scan'}
        </div>
        <div className='text-base font-medium'>{warning.message}</div>
        <div className='text-xs text-white-100/70'>Touch anywhere to dismiss</div>
      </div>
    </div>
  )
}
